import { api } from '../middleware/api';
import { sseClient } from '../../services/sseClient';

export interface FOEAuthorizationPendingItem {
  au_id: number;
  au_company: string;
  au_type: string;
  au_rmid: number;
  au_ciid: number;
  au_value: string;
  au_requester: string;
  au_request_date: string;
  au_status: string;
  au_approver?: string;
  au_approval_date?: string;
  au_remarks?: string;
  au_approval_remarks?: string;
  rm_name: string;
  requester_name: string;
  guest_name: string;
}

export const foauthorizationApi = api
  .enhanceEndpoints({ addTagTypes: ['FOAuthorization'] })
  .injectEndpoints({
    endpoints: builder => ({
      requestAuthorization: builder.mutation<any, any>({
        query: data => ({
          url: 'foeauthorization/request',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['FOAuthorization'],
      }),
      getPendingAuthorizations: builder.query<FOEAuthorizationPendingItem[], void>({
        query: () => 'foeauthorization/pending',
        providesTags: ['FOAuthorization'],
        async onCacheEntryAdded(
          _arg,
          { cacheDataLoaded, cacheEntryRemoved, dispatch }
        ) {
          try {
            await cacheDataLoaded;
            const unsubscribe = sseClient.addListener(data => {
              if (
                data.type === 'AUTHORIZATION_UPDATED' ||
                data.type === 'NOTIFICATION_ADDED' ||
                data.type === 'FRONT_OFFICE_UPDATED'
              ) {
                dispatch(
                  foauthorizationApi.util.invalidateTags(['FOAuthorization'])
                );
              }
            });
            await cacheEntryRemoved;
            unsubscribe();
          } catch {
            // no-op
          }
        },
      }),
      approveAuthorization: builder.mutation<
        any,
        { au_id: string | number; remarks?: string } | string | number
      >({
        query: arg => {
          const au_id = typeof arg === 'object' ? arg.au_id : arg;
          const remarks = typeof arg === 'object' ? arg.remarks : undefined;
          return {
            url: `foeauthorization/approve/${au_id}`,
            method: 'PUT',
            body: { remarks },
          };
        },
        invalidatesTags: ['FOAuthorization'],
      }),
      rejectAuthorization: builder.mutation<
        any,
        { au_id: string | number; remarks?: string } | string | number
      >({
        query: arg => {
          const au_id = typeof arg === 'object' ? arg.au_id : arg;
          const remarks = typeof arg === 'object' ? arg.remarks : undefined;
          return {
            url: `foeauthorization/reject/${au_id}`,
            method: 'PUT',
            body: { remarks },
          };
        },
        invalidatesTags: ['FOAuthorization'],
      }),
      approveAuthorizationsBatch: builder.mutation<
        any,
        { au_ids: (string | number)[]; remarks?: string }
      >({
        query: data => ({
          url: 'foeauthorization/approve-batch',
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: ['FOAuthorization'],
      }),
      rejectAuthorizationsBatch: builder.mutation<
        any,
        { au_ids: (string | number)[]; remarks?: string }
      >({
        query: data => ({
          url: 'foeauthorization/reject-batch',
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: ['FOAuthorization'],
      }),
      getLastCheckoutByRoom: builder.query<any, number | string>({
        query: rmid => `foeauthorization/last-checkout/${rmid}`,
        providesTags: ['FOAuthorization'],
      }),
    }),
    overrideExisting: true,
  });

export const {
  useRequestAuthorizationMutation,
  useGetPendingAuthorizationsQuery,
  useApproveAuthorizationMutation,
  useRejectAuthorizationMutation,
  useApproveAuthorizationsBatchMutation,
  useRejectAuthorizationsBatchMutation,
  useGetLastCheckoutByRoomQuery,
  useLazyGetLastCheckoutByRoomQuery,
} = foauthorizationApi;
